import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { hasDisplayPrice } from '../../../utils/priceDisplay';
import Button from '../../../components/ui/Button';
import { getProductImageUrl } from '../../../utils/productImage';

const ProductCard = ({ product, viewMode = 'grid', onSelect }) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    if (onSelect) {
      onSelect(product);
      return;
    }
    const idOrSlug = product?.slug || product?.id;
    if (!idOrSlug) return;
    navigate(`/product-detail/${idOrSlug}`);
  };

  const getCertificationColor = (cert) => {
    switch (cert) {
      case 'FDA': return 'text-success bg-success/10 border-success/20';
      case 'CE': return 'text-primary bg-primary/10 border-primary/20';
      case 'GDPMD': return 'text-warning bg-warning/10 border-warning/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getAvailabilityStyle = (status) => {
    switch (status) {
      case 'In Stock': return 'text-success bg-success/10 border-success/20';
      case 'Coming Soon':
      case 'Pre-Order': return 'text-warning bg-warning/10 border-warning/20';
      case 'Custom Order': return 'text-primary bg-primary/10 border-primary/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getAvailabilityIcon = (status) => {
    switch (status) {
      case 'In Stock': return 'CheckCircle';
      case 'Coming Soon':
      case 'Pre-Order': return 'Clock';
      default: return 'Package';
    }
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2 }}
        onClick={handleViewDetails}
        className="bg-card border border-border rounded-lg p-6 shadow-medical hover:shadow-medical-hover transition-all duration-300 cursor-pointer group"
      >
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Product Image (no fallback: only API image) */}
          <div className="flex-shrink-0">
            <div className="w-full lg:w-48 h-48 bg-muted rounded-lg overflow-hidden flex items-center justify-center">
              {getProductImageUrl(product?.image) ? (
                <Image
                  src={getProductImageUrl(product?.image)}
                  alt={product?.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              ) : (
                <span className="text-text-secondary text-xs">No image</span>
              )}
            </div>
          </div>

          {/* Product Details */}
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-text-primary group-hover:text-primary transition-colors duration-200 mb-2">
                {product?.name}
              </h3>
              <p className="text-text-secondary text-sm mb-3 line-clamp-2">
                {product?.description}
              </p>
            </div>

            {/* Specifications */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">Model</span>
                <p className="text-sm font-medium text-text-primary">{product?.manufacturer}</p>
              </div>
              {/* <div>
                <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">Model</span>
                <p className="text-sm font-medium text-text-primary">{product?.model}</p>
              </div> */}
            </div>

            {/* Availability & Price */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-border">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getAvailabilityStyle(product?.availability)}`}>
                  <Icon name={getAvailabilityIcon(product?.availability)} size={12} className="mr-1" />
                  {product?.availability}
                </span>
              </div>
              {hasDisplayPrice(product?.price) && (
                <span className="text-lg font-semibold text-text-primary">
                  RM {Number(product.price).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={handleViewDetails}
      className="bg-card border border-border rounded-lg overflow-hidden shadow-medical hover:shadow-medical-hover transition-all duration-300 group cursor-pointer"
    >
      {/* Product Image (no fallback: only API image) */}
      <div className="relative h-48 bg-muted overflow-hidden flex items-center justify-center">
        {getProductImageUrl(product?.image) ? (
          <Image
            src={getProductImageUrl(product?.image)}
            alt={product?.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <span className="text-text-secondary text-xs">No image</span>
        )}
      </div>
      {/* Product Details */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-text-primary line-clamp-1 group-hover:text-primary transition-colors duration-200">
            {product?.name}
          </h3>
          <p className="text-sm text-text-secondary mt-1 line-clamp-2">
            {product?.description}
          </p>
        </div>

        {/* Specifications */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Model:</span>
            <span className="font-medium text-text-primary">{product?.manufacturer}</span>
          </div>
          {/* <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Model:</span>
            <span className="font-medium text-text-primary">{product?.model}</span>
          </div> */}
        </div>

        {/* Availability Status & Price */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-border">
          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getAvailabilityStyle(product?.availability)}`}>
            <Icon name={getAvailabilityIcon(product?.availability)} size={10} className="mr-1" />
            {product?.availability}
          </span>
          {hasDisplayPrice(product?.price) && (
            <span className="text-base font-semibold text-text-primary">
              RM {Number(product.price).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;